import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Screen } from "@/components/Screen";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Thermometer, Shirt, Backpack, MapPin } from "lucide-react";
import { ITINERARY, GENERAL_PACKING, TRIP_TITLE, type ItineraryDay } from "@/data/itinerary";
import { parseLocalDate } from "@/lib/format-date";

export const Route = createFileRoute("/_authenticated/itinerary")({
  head: () => pageMeta({ title: 'Trip Itinerary — Roava by WGP Apps', description: "Plan and coordinate your group's day-by-day trip itinerary.", path: '/itinerary' }),
  component: ItineraryPage,
});

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function ItineraryPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const today = todayISO();
  const activeDay = ITINERARY.find((d) => d.date === today);
  const activeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  return (
    <Screen
      title={t("itinerary.title")}
      subtitle={t("itinerary.subtitle")}
      action={
        <Button variant="ghost" size="icon" aria-label={t("itinerary.back_to_trip")} onClick={() => nav({ to: "/trip" })}>
          <ArrowLeft className="size-5" />
        </Button>
      }
    >
      <div className="rounded-3xl bg-gradient-to-br from-primary to-sky-accent p-5 text-primary-foreground shadow-lg shadow-primary/20">
        <p className="text-xs uppercase tracking-wider opacity-80">{t("itinerary.trip_tag")}</p>
        <p className="mt-1 text-lg font-semibold leading-snug">{TRIP_TITLE}</p>
        {activeDay && (
          <p className="mt-3 text-sm bg-white/15 rounded-xl px-3 py-2 inline-block">
            {t("itinerary.today_label", { day: activeDay.day, title: activeDay.title })}
          </p>
        )}
      </div>

      <div className="mt-5 grid gap-4">
        {ITINERARY.map((d) => (
          <DayCard key={d.day} day={d} isToday={d.date === today} refEl={d.date === today ? activeRef : undefined} />
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-card p-4">
        <div className="flex items-center gap-2 mb-2">
          <Backpack className="size-4 text-muted-foreground" />
          <h2 className="font-semibold">{t("itinerary.general_packing")}</h2>
        </div>
        <p className="text-sm text-muted-foreground">{GENERAL_PACKING.intro}</p>
        <ul className="mt-3 grid gap-1.5 text-sm">
          {GENERAL_PACKING.items.map((it) => (
            <li key={it} className="flex gap-2">
              <span className="text-muted-foreground">•</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>
    </Screen>
  );
}

function DayCard({
  day,
  isToday,
  refEl,
}: {
  day: ItineraryDay;
  isToday: boolean;
  refEl?: React.RefObject<HTMLDivElement | null>;
}) {
  const { t } = useTranslation();
  return (
    <div
      ref={refEl}
      className={`rounded-2xl border bg-card p-4 ${
        isToday ? "border-primary ring-2 ring-primary/30" : "border-border"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`size-12 shrink-0 rounded-2xl grid place-items-center font-bold ${
            isToday ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground"
          }`}
        >
          {day.day}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Calendar className="size-3" />
            {formatLong(day.date)}
            {day.distanceKm != null && (
              <>
                <span className="mx-1">·</span>
                <MapPin className="size-3" /> {t("itinerary.distance_km", { km: day.distanceKm })}
              </>
            )}
          </p>
          <h2 className="mt-0.5 font-semibold leading-tight">{day.title}</h2>
        </div>
      </div>

      <div className="mt-3 grid gap-2 text-sm">
        {day.body.map((p, i) => (
          <p key={i} className="leading-relaxed">
            {p}
          </p>
        ))}
      </div>

      <div className="mt-4 rounded-xl bg-muted/40 p-3">
        <div className="flex items-center gap-2 mb-2">
          <Thermometer className="size-4 text-muted-foreground" />
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("itinerary.expected_weather")}</p>
        </div>
        <div className="grid gap-2">
          {day.weather.map((w, i) => (
            <div key={i} className="text-sm">
              <p className="font-medium">{w.label}</p>
              <ul className="text-muted-foreground text-xs mt-0.5 grid gap-0.5">
                {w.day && <li>{t("itinerary.weather_day")}: {w.day}</li>}
                {w.evening && <li>{t("itinerary.weather_evening")}: {w.evening}</li>}
                {w.night && <li>{t("itinerary.weather_night")}: {w.night}</li>}
                {w.morning && <li>{t("itinerary.weather_morning")}: {w.morning}</li>}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 rounded-xl bg-muted/40 p-3">
        <div className="flex items-center gap-2 mb-2">
          <Shirt className="size-4 text-muted-foreground" />
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("itinerary.suggested_clothing")}</p>
        </div>
        <ul className="grid gap-1 text-sm">
          {day.clothing.map((c) => (
            <li key={c} className="flex gap-2">
              <span className="text-muted-foreground">•</span>
              <span>{c}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function formatLong(iso: string) {
  return parseLocalDate(iso).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
